import { Locale } from "@omni/types/enums/locale.enum";
import { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { Modal } from "@/components/General/Modal/modal";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import { load } from "@/redux/modules/api/data/stores/shopNames/shopNames.module";
import { getShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import * as LocaleSelectors from "@/redux/modules/value/locales/locales.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { setLanguageCookie } from "@/services/cookie.service";

import EditPreferencesForm from "./modal.component";

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

const EditPreferencesFormContainer = ({ onClose, isOpen }: Props) => {
    const { t } = useGranite();
    const customer = useSelector(CustomerSelectors.getCustomer);
    const languages = useSelector(LocaleSelectors.getLanguages);
    const shopNames = useSelector(getShopNames, shallowEqual);
    const isLoading = useSelector(CustomerSelectors.isLoading);
    const dispatch = useThunkDispatch();

    useEffect(() => {
        if (shopNames.length > 0) return;
        dispatch(load());
    }, [shopNames, dispatch]);

    const onSubmit = (locale: Locale, favouriteStoreId?: string) => {
        clearMessageById("edit-preferences-success");
        clearMessageById("edit-preferences-failure");

        dispatch(
            CustomerModule.updateFromData({
                ...customer!,
                language: locale,
                favouriteStoreId
            })
        ).then((action) => {
            if (action.type === CustomerModule.APIStatus.SUCCESS) {
                const language = languages.find((l) => l.locale === locale);
                if (customer?.language !== locale && language) {
                    setLanguageCookie(language["language-code"]);
                    window.location.href = `${language.url}?editpreferencessuccess=true`;
                    return;
                }
                broadcastMessage({
                    id: "edit-preferences-success",
                    text: t("general.profile.editpreferences.modal.success"),
                    status: "success",
                    dismissible: true,
                    activeScroll: true
                });
                onClose();
            } else {
                broadcastMessage({
                    id: "edit-preferences-failure",
                    text: t("general.profile.editpreferences.modal.failure"),
                    status: "danger",
                    dismissible: true
                });
            }
        });
    };

    if (!customer || languages.length === 0 || shopNames.length === 0)
        return null;

    return (
        <Modal
            open={isOpen}
            setOpen={onClose}
            formId={"profileEditPreferencesForm"}
            isLoading={isLoading}
            confirmButtonText={t(
                "general.profile.editpreferences.modal.submit"
            )}
            title={t("general.profile.editpreferences.modal.title")}
            subtitle={t("general.profile.editpreferences.modal.subtitle")}
        >
            <EditPreferencesForm
                languages={languages}
                customer={customer}
                onSubmit={onSubmit}
            />
        </Modal>
    );
};

export default EditPreferencesFormContainer;
